import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { DndContext, closestCenter, MeasuringStrategy } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  defaultAnimateLayoutChanges,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";

import PostAttachmentCard from "../PostAttachmentCard";
import { generateRandomId } from "Utils";

const style = {
  editAttachmentModalBody: {
    display: "flex",
    gap: "16px",
    flexDirection: "column",
  },
};

export default function ReorderMedia({
  postAttachments,
  setPostAttachments,
  setToastMsg,
  studentActivityData,
  studentIds,
  selectedStudentForActivity,
  isActivityTrackingPost = false,
  updatePost,
  studentActivities,
}) {
  const [activeMediaId, setActiveMediaId] = useState(null);
  const [
    studentActivityAttachmentData,
    setStudentActivityAttachmentData,
  ] = useState(studentActivityData);
  useEffect(() => {
    setActiveMediaId(null);
  }, [postAttachments]);
  const handleDragStart = ({ active }) => {
    setActiveMediaId(_.get(active, "id"));
  };

  const handleDragEnd = ({ active, over }) => {
    const updatedOptions = arrayMove(
      postAttachments,
      _.get(active, "data.current.index"),
      _.get(over, "data.current.index")
    );
    setActiveMediaId(null);
    setPostAttachments(updatedOptions);
  };

  const handleDragEndActivityAttachment = ({ active, over }) => {
    const updatedStudentActivities = _.cloneDeep(studentActivityAttachmentData);
    if (selectedStudentForActivity === "ALL") {
      const updatedAttachments = arrayMove(
        _.get(updatedStudentActivities, ["ALL", "attachments"], []),
        _.get(active, "data.current.index"),
        _.get(over, "data.current.index")
      );
      _.setWith(
        updatedStudentActivities,
        ["ALL", "attachments"],
        updatedAttachments,
        Object
      );

      _.forEach(studentIds, studentId => {
        const oldCommonAttachments = _.get(
          studentActivityAttachmentData,
          ["ALL", "attachments"],
          []
        );
        const studentAttachments = _.get(
          studentActivityAttachmentData,
          [studentId, "attachments"],
          []
        );

        if (_.size(oldCommonAttachments) !== _.size(studentAttachments)) {
          return;
        }

        for (const attachmentIndex in oldCommonAttachments) {
          const oldAttachmentUrl = _.get(oldCommonAttachments, [
            attachmentIndex,
            "value",
            "url",
          ]);
          const studentAttachmentUrl = _.get(studentAttachments, [
            attachmentIndex,
            "value",
            "url",
          ]);

          if (oldAttachmentUrl !== studentAttachmentUrl) {
            return;
          }
        }

        const updatedAttachments = arrayMove(
          _.get(updatedStudentActivities, [studentId, "attachments"], []),
          _.get(active, "data.current.index"),
          _.get(over, "data.current.index")
        );
        _.setWith(
          updatedStudentActivities,
          [studentId, "attachments"],
          updatedAttachments,
          Object
        );
        _.set(
          updatedStudentActivities,
          [studentId, "attachments"],
          updatedAttachments
        );
      });
    } else {
      //change this to use studentIds
      const updatedAttachments = arrayMove(
        _.get(
          updatedStudentActivities,
          [selectedStudentForActivity, "attachments"],
          []
        ),
        _.get(active, "data.current.index"),
        _.get(over, "data.current.index")
      );
      _.set(
        updatedStudentActivities,
        [selectedStudentForActivity, "attachments"],
        updatedAttachments
      );
    }
    setStudentActivityAttachmentData(updatedStudentActivities);
    setPostAttachments(updatedStudentActivities);
  };

  const handleDelete = ({ id }) => {
    setToastMsg({
      msg: "journal:media_delete_message",
      type: "success",
      locale_params: [
        {
          key: "mediaName",
          value: _.get(_.find(postAttachments, { id }), "name"),
          isPlainText: true,
        },
      ],
    });
    setPostAttachments(_.filter(postAttachments, option => option.id !== id));
  };

  const animateLayoutChanges = args =>
    defaultAnimateLayoutChanges({ ...args, wasDragging: true });

  const getActivityAttachments = useMemo(() => {
    let activityAttachments = _.map(
      _.filter(
        _.get(
          studentActivities,
          [
            selectedStudentForActivity === "ALL"
              ? "ALL"
              : selectedStudentForActivity,
            "attachments",
          ],
          []
        ),
        attachment => attachment.value.type !== "AUDIO"
      ),
      "value"
    );
    activityAttachments = _.map(activityAttachments, attachment => ({
      id: generateRandomId(),
      ...attachment,
    }));
    return activityAttachments;
  }, [studentActivities, selectedStudentForActivity]);

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={
        isActivityTrackingPost ? handleDragEndActivityAttachment : handleDragEnd
      }
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      modifiers={[restrictToParentElement, restrictToVerticalAxis]}
    >
      <div style={style.editAttachmentModalBody}>
        <SortableContext
          items={
            isActivityTrackingPost ? getActivityAttachments : postAttachments
          }
        >
          {_.map(
            isActivityTrackingPost ? getActivityAttachments : postAttachments,
            (media, mediaIndex) => {
              const tempId = generateRandomId();
              return (
                <PostAttachmentCard
                  isActive={_.get(media, "id", tempId) === activeMediaId}
                  key={_.get(media, "id", tempId)}
                  id={_.get(media, "id", tempId)}
                  mediaIndex={mediaIndex}
                  animateLayoutChanges={animateLayoutChanges}
                  handleDelete={handleDelete}
                  media={media}
                />
              );
            }
          )}
        </SortableContext>
      </div>
    </DndContext>
  );
}
